// Logo de la empresa en el voucher impreso: se deja listo para la impresora térmica de las apps
// (escala de grises sobre fondo blanco, ancho del papel) antes de enviarlo al API.

// Ancho con el que las apps ya imprimen el logo (cabe en el papel de 58 mm) y alto máximo para no gastar papel
export const PRINT_LOGO_MAX_WIDTH = 350;
export const PRINT_LOGO_MAX_HEIGHT = 160;
// Límite del API para la imagen ya procesada
export const PRINT_LOGO_MAX_BYTES = 300 * 1024;
// Archivo que se acepta subir (antes de procesar)
export const PRINT_LOGO_MAX_FILE_BYTES = 5 * 1024 * 1024;
export const PRINT_LOGO_ACCEPT = 'image/png,image/jpeg,image/webp';

// Cuánto se puede agrandar un logo pequeño (más se vería pixelado)
export const PRINT_LOGO_MAX_UPSCALE = 4;
// Lado máximo con que se lee el archivo original antes de recortarlo
const SOURCE_MAX_SIDE = 2000;
// Un pixel más claro que esto se considera fondo (papel) al recortar márgenes
const BACKGROUND_GRAY = 240;

// Tamaño final conservando la proporción: ocupa el ancho del papel o el alto máximo,
// agrandando los logos pequeños hasta PRINT_LOGO_MAX_UPSCALE veces
export const fitPrintLogo = (width: number, height: number) => {
    if (!width || !height) return { width: 0, height: 0 };
    const scale = Math.min(PRINT_LOGO_MAX_UPSCALE, PRINT_LOGO_MAX_WIDTH / width, PRINT_LOGO_MAX_HEIGHT / height);
    return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

// Tamaño real de un data URL en base64
export const dataUrlBytes = (dataUrl: string) => {
    const data = dataUrl.split(',')[1] ?? '';
    return Math.floor(data.length * 3 / 4) - (data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0);
};

// Mensaje de error del archivo elegido, o null si se puede procesar
export const printLogoFileError = (file: { type: string; size: number }) => {
    if (!PRINT_LOGO_ACCEPT.split(',').includes(file.type)) return 'El logo debe ser una imagen PNG, JPG o WEBP.';
    if (file.size > PRINT_LOGO_MAX_FILE_BYTES) return 'La imagen no debe pasar de 5 MB.';
    return null;
};

// Pixeles RGBA a escala de grises sobre blanco (la transparencia se imprime como papel)
export const toPrintableGray = (pixels: Uint8ClampedArray) => {
    for (let i = 0; i < pixels.length; i += 4) {
        const alpha = pixels[i + 3] / 255;
        const gray = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
        const value = Math.round(gray * alpha + 255 * (1 - alpha));
        pixels[i] = pixels[i + 1] = pixels[i + 2] = value;
        pixels[i + 3] = 255;
    }
    return pixels;
};

// Rectángulo con el dibujo del logo (pixeles en gris ya preparados), sin el margen blanco.
// null si la imagen está toda en blanco.
export const printableBounds = (pixels: Uint8ClampedArray, width: number, height: number) => {
    let left = width, top = height, right = -1, bottom = -1;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (pixels[(y * width + x) * 4] < BACKGROUND_GRAY) {
                if (x < left) left = x;
                if (x > right) right = x;
                if (y < top) top = y;
                if (y > bottom) bottom = y;
            }
        }
    }
    if (right < 0) return null;
    return { x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
};

const loadImage = (file: File) => new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen.')); };
    img.src = url;
});

// Archivo elegido -> PNG en escala de grises del tamaño del papel (data URL)
export const preparePrintLogo = async (file: File): Promise<string> => {
    const fileError = printLogoFileError(file);
    if (fileError) throw new Error(fileError);

    const img = await loadImage(file);
    if (!img.naturalWidth || !img.naturalHeight) throw new Error('No se pudo leer la imagen.');

    // 1. Original en gris sobre blanco (las imágenes muy grandes se reducen para no gastar memoria)
    const sourceScale = Math.min(1, SOURCE_MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const source = document.createElement('canvas');
    source.width = Math.max(1, Math.round(img.naturalWidth * sourceScale));
    source.height = Math.max(1, Math.round(img.naturalHeight * sourceScale));
    const sourceCtx = source.getContext('2d');
    if (!sourceCtx) throw new Error('El navegador no permite procesar la imagen.');
    sourceCtx.drawImage(img, 0, 0, source.width, source.height);
    const data = sourceCtx.getImageData(0, 0, source.width, source.height);
    toPrintableGray(data.data);
    sourceCtx.putImageData(data, 0, 0);

    // 2. Se quita el margen blanco para que el logo ocupe el ancho del papel
    const bounds = printableBounds(data.data, source.width, source.height);
    if (!bounds) throw new Error('La imagen está en blanco; use un logo con contraste.');
    const { width, height } = fitPrintLogo(bounds.width, bounds.height);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('El navegador no permite procesar la imagen.');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, bounds.x, bounds.y, bounds.width, bounds.height, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/png');
    if (dataUrlBytes(dataUrl) > PRINT_LOGO_MAX_BYTES) throw new Error('El logo procesado pasa de 300 KB; use una imagen más simple.');
    return dataUrl;
};
